
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@supabase/auth-helpers-react";
import { Crown } from "lucide-react";
import { useChurchBranding } from "@/contexts/ChurchBrandingContext";
import { useToastAction } from "@/hooks/useToastAction";

export interface Verse {
  text: string;
  reference: string; 
  version: string;
}

export function useVerseData() {
  const [verse, setVerse] = useState<Verse>({ text: "", reference: "", version: "KJV" });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const session = useSession();
  const { branding } = useChurchBranding();
  const { createToastAction } = useToastAction();

  const fetchRandomVerse = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('get-verse');

      if (error) {
        console.error('Error fetching verse:', error);
        toast({
          title: "Error",
          description: "Failed to load verse. Please try again later.",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        console.log('Received verse data:', data);
        setVerse(data);
      } else {
        toast({
          title: "No verses found",
          description: "Unable to load a verse at this time.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRandomVerse();
  }, []);

  const handleNextVerse = async () => {
    if (!session) {
      const churchName = branding?.churchName || "TEST COMM";
      toast({
        title: "Sign Up",
        description: `Join us at ${churchName} for free to access more verses and features!`,
        action: createToastAction({
          label: "Sign Up",
          onClick: () => navigate("/premium"),
          icon: <Crown className="w-4 h-4" />,
        }),
        duration: Infinity, // Make the toast stay until dismissed
      });
      return;
    }

    await fetchRandomVerse();
  };

  return { verse, loading, handleNextVerse };
}
